// @ts-nocheck
import { signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import SubmitArrow from "../Svgs/SubmitArrow.svg";
import { BorderBeam } from "../components/BorderBeam";
import ChatLog from "../components/ChatLog";
import ModelSelector from "../components/ModelSelector";
import CockpitMenu from "../components/CockpitMenu";
import Error from "../components/Error";
import Header from "../components/Header";
import IntroSection from "../components/IntroSection";
import MicrophoneButton from "../components/MicrophoneButton";
import SmartAIMenu from "../components/SmartAIMenu";
import { Button } from "../components/ui/button";
import { AuthContext } from "../context/AuthContext";
import { useThread } from "../context/ThreadContext";
import { auth, db } from "../firebase.config";
import { MODEL_ICONS, modelMapping } from "../lib/ModelIcons";
import { FolderService } from "../services/FolderService";
import { ThreadService } from "../services/ThreadService";
import ImageUploader from "../components/ImageUploader";
import MobileNavigation from "../components/MobileNavigation";
import SideMenu from "../components/SideMenu";
import SmartAIButton from "../components/SmartAIButton";
import {
  buildPromptWithInstructions,
  getSystemMessage,
} from "../lib/Promptbuilder";
import {
  processDocumentIfNeeded,
  processResponseStream,
} from "../lib/Processor";
import { motion, AnimatePresence } from "framer-motion";
import { useDebouncedCallback } from "../hooks/use-debounce-callback";

const DEFAULT_MODEL = "OpenAI";
const DEFAULT_ORG_LOGO = "/X.png";
const DEFAULT_ORG_DARK_LOGO = "/X_dark.png";
const DEFAULT_ORG_NAME = "Xeeds.AI";

// Constants
const DEFAULT_MODEL_PARAMETERS = {
  temperature: 0.7,
  top_p: 1,
  max_tokens: 4000,
  frequency_penalty: 0,
  presence_penalty: 0,
  system_prompt: "",
  logit_bias: 0,
};

const DEFAULT_SMART_AI_SETTINGS = {
  multipleResponses: false,
  promptAutoImprove: false,
  industry: "",
  trait: "",
  role: "",
  memory: "Full",
};

const providerIconIndexMapping = {
  openai: 0,
  claude: 1,
  gemini: 2,
  grok: 3,
  mistral: 4,
  llama: 5,
  perplexity:6,
  deepseek:7,
  cohere:8,
  "other ai's": 9
};

const Home = () => {
  const { currentUser, theme, dispatch } = useContext(AuthContext);
  const { state, dispatch: threadDispatch } = useThread();
  const { currentThread } = state;

  const [isAnimating, setIsAnimating] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [hideMenu, setHideMenu] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState(DEFAULT_MODEL);
  const [orgLogo, setOrgLogo] = useState(DEFAULT_ORG_LOGO); // Default logo
  const [orgLogo_dark, setOrgLogoDark] = useState(DEFAULT_ORG_DARK_LOGO); // Default logo
  const [orgName, setOrgName] = useState(DEFAULT_ORG_NAME); // Default name
  const [lastMessageId, setLastMessageId] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [webBrowsing, setWebBrowsing] = useState(false);
  const [showCockpit, setShowCockpit] = useState(false);
  const [showSmartAI, setShowSmartAI] = useState(false);
  const [imageData, setImageData] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [isModelSelecting, setIsModelSelecting] = useState(false);
  const [currentModelIcon, setCurrentModelIcon] = useState(0);
  const [selectedModelIconIndex, setSelectedModelIconIndex] = useState(0);
  const [modelParameters, setModelParameters] = useState(
    DEFAULT_MODEL_PARAMETERS
  );
  const [smartAISettings, setSmartAISettings] = useState(
    DEFAULT_SMART_AI_SETTINGS
  );
  const [relatedQueries, setRelatedQueries] = useState([]);
  const [citations, setCitations] = useState([]);
  const [voiceMode, setVoiceMode] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const chatLogRef = useRef(null);
  const chatLogEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);
  const micButtonRef = useRef(null);

  useEffect(() => {
    let animationInterval;

    if (isModelSelecting) {
      // Start cycling through model icons every 2000ms (matching animation duration)
      animationInterval = setInterval(() => {
        setCurrentModelIcon((prev) => (prev + 1) % MODEL_ICONS.length);
      }, 450); // Match this with the CSS animation duration
    }

    return () => {
      if (animationInterval) {
        clearInterval(animationInterval);
      }
    };
  }, [isModelSelecting]);

  useEffect(() => {
    if (chatLogEndRef.current) {
      chatLogEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [currentThread?.messages]); // This will trigger on thread change or new messages

  const models = Object.entries(modelMapping).reduce(
    (acc, [model, company]) => {
      if (!acc[company]) acc[company] = [];
      acc[company].push(model);
      return acc;
    },
    {}
  );

  useEffect(() => {
    // Load user's threads on component mount
    const loadThreadsAndFolders = async () => {
      try {
        const [threads, folders] = await Promise.all([
          ThreadService.getUserThreads(currentUser.uid),
          FolderService.getUserFolders(currentUser.uid),
        ]);
        threadDispatch({ type: "SET_THREADS", payload: threads });
        threadDispatch({ type: "SET_FOLDERS", payload: folders });
      } catch (err) {
        setError(err);
      }
    };
    if (currentUser?.uid) {
      loadThreadsAndFolders();
    }
  }, [currentUser?.uid, threadDispatch]);

  useEffect(() => {
    const loadOrganizationDetails = async () => {
      try {
        if (currentUser?.org_id) {
          const orgDoc = await getDoc(
            doc(db, "organizations", currentUser.org_id)
          );
          if (orgDoc.exists()) {
            const orgData = orgDoc.data();
            if (orgData.logo_url) {
              setOrgLogo(orgData.logo_url);
            }
            if (orgData.name) {
              setOrgName(orgData.name);
            }
          }
        }
      } catch (err) {
        console.error("Error loading organization details:", err);
        // Keep default logo and name if there's an error
      }
    };

    if (currentUser?.org_id) {
      loadOrganizationDetails();
    }
  }, [currentUser?.org_id]);

  // Add this useEffect to focus the input when component mounts or when URL changes
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Add this useEffect to refocus after a message is sent
  useEffect(() => {
    if (!isLoading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading]);

  useEffect(() => {
    resetSmartAISettings();
    setRelatedQueries([]);
    setCitations([]);
  }, [currentThread?.id]);

  const resetSmartAISettings = useCallback(() => {
    setSmartAISettings(DEFAULT_SMART_AI_SETTINGS);
  }, []);

  const handleNewChat = useCallback(() => {
    threadDispatch({ type: "SET_CURRENT_THREAD", payload: null });
    setRelatedQueries([]);
    setCitations([]);
    resetSmartAISettings();
  }, [threadDispatch, resetSmartAISettings]);

  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth);
      dispatch({ type: "LOGOUT" });
      localStorage.removeItem("token");
      navigate("/auth/login");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  }, [dispatch, navigate]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (prompt.trim() && !isLoading) {
          handleSubmit(e);
        }
      }
    },
    [prompt, isLoading]
  );

  const handleTranscriptionComplete = (transcript) => {
    setPrompt(transcript);

    if (transcript.trim() && !isLoading) {
      const syntheticEvent = { preventDefault: () => { } };
      handleSubmit(syntheticEvent);
    }
  };

  const handleImageUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setImageLoading(true);
      const reader = new FileReader();

      reader.onload = () => {
        setSelectedImage(file);
        setImageData(reader.result);
        setImageLoading(false);
      };

      reader.onerror = () => {
        console.error("Error reading file");
        setImageLoading(false);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error processing file:", error);
      setImageLoading(false);
    }
  }, []);

  const handleModelAutoSelect = useCallback(async () => {
    console.log("Auto-selecting model based on prompt:", prompt);
    const token = await auth.currentUser.getIdToken(true);
    if (!prompt || isModelSelecting) return;

    try {
      setIsModelSelecting(true);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/model-selector`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ prompt }),
        }
      );
      console.log("Model selection response:", response);

      if (!response.ok) {
        throw new Error("Failed to get model recommendation");
      }

      const data = await response.json();
      const recommendedModel = data.recommendedModel;

      // Find which company this model belongs to
      let selectedCompany = null;
      for (const [company, modelList] of Object.entries(models)) {
        if (modelList.includes(recommendedModel)) {
          selectedCompany = company;
          break;
        }
      }

      if (selectedCompany) {
        setSelectedModel(`${selectedCompany} - ${recommendedModel}`);
      } else {
        const fallbackCompany = modelMapping[recommendedModel] || "OpenAI";
        setSelectedModel(fallbackCompany);
      }
    } catch (error) {
      console.error("Error selecting model:", error);
    } finally {
      setIsModelSelecting(false);
    }
  }, [prompt, isModelSelecting]);

  const handleSpeech = async (text) => {
    if (isPlaying && audioRef.current) {
      // If already playing, stop the audio
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      return;
    }

    setIsLoading(true);

    try {
      const options = {
        method: "POST",
        headers: {
          // Authorization:
          //   "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOiI2NzVhOTg4N2FiMThmODE5OGE1NTkzNDUiLCJ0eXBlIjoiYXBpS2V5IiwiaWF0IjoxNzMzOTkwNTM1LCJleHAiOjQ4ODk3NTA1MzV9.JD0MNTurZNkorKB4vYdL0btyZKy-npR27FKSoKVaBdU",
          "Content-Type": "application/json",
          "xi-api-key": process.env.REACT_APP_ELEVENLABS_API,
        },
        body: JSON.stringify({
          text: text,
          model_id: "eleven_multilingual_v2",
          voice_id: "zT03pEAEi0VHKciJODfn",
          // voice_id: "karan",
          // add_wav_header: true,
          // sample_rate: 16000,
          // speed: 1,
          // language: "en",
        }),
      };

      // First, try to get the response as JSON to check if there's an error message
      const response = await fetch(
        "https://api.elevenlabs.io/v1/text-to-speech/JBFqnCBsd6RMkjVDRZzb?output_format=mp3_44100_128",
        options
      );

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      // Clone the response to use it twice
      const responseClone = response.clone();

      try {
        // Try to parse as JSON first to see if it's an error response
        const jsonData = await responseClone.json();
        console.log("API response:", jsonData);

        if (jsonData.error) {
          throw new Error(`API error: ${jsonData.error}`);
        }

        // If we have audio_base64, use that
        if (jsonData.audio_base64) {
          const audioSrc = `data:audio/wav;base64,${jsonData.audio_base64}`;

          if (!audioRef.current) {
            audioRef.current = new Audio(audioSrc);

            audioRef.current.addEventListener("ended", () => {
              setIsPlaying(false);
            });

            audioRef.current.addEventListener("error", (e) => {
              console.error("Audio playback error:", e);
              setIsPlaying(false);
            });
          } else {
            audioRef.current.src = audioSrc;
          }

          await audioRef.current.play();
          setIsPlaying(true);
          return;
        }
      } catch (jsonError) {
        // Not JSON, so it's probably binary data
        console.log("Not JSON response, trying as binary data");
      }

      // If we get here, try to handle as binary data
      const arrayBuffer = await response.arrayBuffer();

      // Create a Blob with the correct MIME type
      const blob = new Blob([arrayBuffer], { type: "audio/wav" });

      // Create a URL for the blob
      const audioUrl = URL.createObjectURL(blob);

      console.log("Created audio URL:", audioUrl);

      if (audioRef.current) {
        // Clean up previous objectURL
        if (audioRef.current.src && audioRef.current.src.startsWith("blob:")) {
          URL.revokeObjectURL(audioRef.current.src);
        }
        audioRef.current.src = audioUrl;
      } else {
        audioRef.current = new Audio(audioUrl);

        audioRef.current.addEventListener("error", (e) => {
          console.error("Audio playback error:", e);
          setIsPlaying(false);
        });
      }

      // Add a debug log before playing
      console.log("About to play audio from URL:", audioRef.current.src);
      audioRef.current.addEventListener("ended", () => {
        console.log("Audio playback ended");

        setIsPlaying(false);
        if (micButtonRef.current) {
          console.log("Triggering click on mic button");

          micButtonRef.current.triggerClick();
        }
      });

      // Try to play the audio
      try {
        await audioRef.current.play();
        setIsPlaying(true);
      } catch (playError) {
        console.error("Failed to play audio:", playError);
        throw new Error("Failed to play audio");
      }
    } catch (error) {
      console.error("Error generating speech:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = useDebouncedCallback(async (e) => {
    if (currentThread?.messages.length > 0) setIsAnimating(true);
    e.preventDefault();

    // Close both menus when sending a message
    setShowSmartAI(false);
    setShowCockpit(false);
    setRelatedQueries([]);
    setCitations([]);

    if (!prompt) return;

    try {
      setIsLoading(true);

      // Get the current Firebase user token
      const token = await auth.currentUser.getIdToken(true);

      // Prepare the actual message to send to the API
      let actualPrompt = buildPromptWithInstructions(prompt, smartAISettings);

      // Define system message for charts and graphs
      const systemMessage = getSystemMessage();

      // If no current thread, create new one
      let threadId = currentThread?.id;
      let threadMessages = currentThread?.messages || [];

      if (!threadId) {
        // Note: We store the original prompt in the thread, not the modified one
        threadId = await ThreadService.createThread(
          currentUser.uid,
          prompt,
          !!imageData,
          selectedImage?.name // Pass the filename
        );
        const newThread = await ThreadService.getUserThreads(currentUser.uid);

        // Fix: Make sure we're getting the correct thread with all properties
        const currentNewThread = newThread.find((t) => t.id === threadId);

        threadDispatch({ type: "SET_THREADS", payload: newThread });
        threadDispatch({
          type: "SET_CURRENT_THREAD",
          payload: currentNewThread,
        });

        // Add system message with instructions for charts and graphs to Firebase
        await ThreadService.addMessageToThread(threadId, systemMessage);

        // Since we just created the thread, set the messages array with the system message
        threadMessages = [systemMessage];
      } else {
        // Add user message to existing thread - store original prompt
        const userMessage = {
          role: "user",
          content: prompt,
          hasImage: !!imageData,
          imageName: selectedImage?.name, // Store the filename
        };
        await ThreadService.addMessageToThread(threadId, userMessage);
        threadDispatch({
          type: "ADD_MESSAGE",
          payload: { threadId, message: userMessage },
        });

        // If we're using an existing thread, check if it has a system message
        const hasSystemMessage = threadMessages.some(
          (msg) => msg.role === "system"
        );
        if (!hasSystemMessage) {
          // Add system message if it doesn't exist
          await ThreadService.addMessageToThread(threadId, systemMessage);
          threadMessages = [systemMessage, ...threadMessages];
        }
      }

      setPrompt(""); // Clear input early

      // Add temporary bot message for streaming
      const tempBotMessage = { role: "assistant", content: "" };
      threadDispatch({
        type: "ADD_MESSAGE",
        payload: { threadId, message: tempBotMessage },
      });

      // Extract just the model name from the selectedModel string (which is in format "Company - model")
      const modelName = selectedModel.split(" - ")[1] || selectedModel;

      // Determine if the file is a supported document type (PDF or CSV)
      const isDocument =
        selectedImage &&
        (selectedImage.type === "application/pdf" ||
          selectedImage.type === "text/csv");

      const parsedDocument = await processDocumentIfNeeded(
        selectedImage,
        imageData,
        token,
        threadId,
        threadDispatch,
        ThreadService
      );

      if (modelName == "o3-mini") {
        setModelParameters((prev) => ({
          ...prev,
          system_prompt: "You are a helpful assistant. When answering questions, include detailed reasoning inside <think>...</think> tags before providing the final answer. Be explicit in your thought process."
        }));
      } else {
        setModelParameters((prev) => ({
          ...prev,
          system_prompt: DEFAULT_MODEL_PARAMETERS.system_prompt
        }));
      }

      // Create fetch for streaming - send the document context if available
      const response = await fetch(`${process.env.REACT_APP_API_URL}/respond`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: actualPrompt,
          threadMessages: threadMessages,
          selectedModel: modelName,
          imageData: !isDocument ? imageData : null,
          fileData: isDocument ? imageData : null,
          fileType: selectedImage?.type,
          parameters: modelParameters,
          threadDocumentContext: currentThread?.documentContext, // Send document context
        }),
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          setError("Authentication error. Please try again.");
          return;
        }
        throw new Error("Failed to get response");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let botResponseText = "";

      // Process the stream
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n\n");

        for (const line of lines) {
          if (line.startsWith("data:")) {
            try {
              const data = JSON.parse(line.slice(5));

              if (data.chunk) {
                botResponseText += data.chunk;

                // Update the streaming message in the UI
                threadDispatch({
                  type: "UPDATE_STREAMING_MESSAGE",
                  payload: { threadId, content: botResponseText },
                });
              }

              // If streaming is complete
              if (data.done) {
                // Add bot response to Firebase
                await ThreadService.addMessageToThread(threadId, {
                  role: "assistant",
                  content: data.fullResponse || botResponseText,
                });

                setCitations(data.citations)

                if (voiceMode) {
                  handleSpeech(botResponseText);
                }

                // We don't need to update the UI again since we've been updating it in real-time
              }
            } catch (e) {
              console.error("Error parsing SSE data", e);
            }
          }
        }
      }

      const relatedQueriesResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/related-queries`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            query: prompt,
            selectedModel,
            parameters: modelParameters,
          }),
        }
      );

      const queryResponse = await relatedQueriesResponse.json();
      setRelatedQueries(queryResponse.relatedQueries);
    } catch (error) {
      console.error("Chat error:", error);
      setError(error.message || "Failed to send message");
      if (error.message.includes("auth") || error.message.includes("token")) {
        navigate("/auth/login");
      }
    } finally {
      setIsLoading(false);
      // Clear image data after submission
      setSelectedImage(null);
      setImageData(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, 800);

  useEffect(() => {
    if (!showMenu) {
      setTimeout(() => {
        setHideMenu(false);
      }, 500);
    } else {
      setHideMenu(true);
    }
  }, [showMenu]);

  return (
    <>
      {showMenu && (
        <MobileNavigation
          showMenu={showMenu}
          setShowMenu={setShowMenu}
          currentThread={currentThread}
          threadDispatch={threadDispatch}
        />
      )}

      {hideMenu && (
        <SideMenu
          currentThread={currentThread}
          threadDispatch={threadDispatch}
          setShowMenu={setShowMenu}
          showMenu={showMenu}
        />
      )}

      <section className="chatBox">
        <Header
          orgLogo={orgLogo}
          orgLogo_dark={orgLogo_dark}
          orgName={orgName}
          handleLogout={handleLogout}
          voiceMode={voiceMode}
          setVoiceMode={setVoiceMode}
          setShowMenu={setShowMenu}
          showMenu={showMenu}
        />

        {currentThread?.messages.length > 0 ? (
          <ChatLog
            currentThread={currentThread}
            isLoading={isLoading}
            relatedQueries={relatedQueries}
            citations={citations}
            setPrompt={setPrompt}
          />
        ) : (
          <IntroSection />
        )}
        <AnimatePresence>
          {!isAnimating && (
            <motion.form
              layout="position"
              transition={{
                type: "spring",
                stiffness: 100,
                damping: 25,
                duration: 0.3,
              }}
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -60 }}
              onAnimationComplete={() =>
                setTimeout(() => {
                  setIsAnimating(false);
                }, 300)
              }
              onSubmit={handleSubmit}
            >
              <div className="inputPromptWrapper relative">
                <div
                  className="overflow-hidden absolute inset-0"
                  style={{ zIndex: 0 }}
                >
                  <BorderBeam
                    size={200}
                    duration={8}
                    colorFrom={theme === "light" ? "#565869" : "#565869"}
                    colorTo={theme === "light" ? "#2d2d3a" : "#2d2d3a"}
                    style={{
                      zIndex: 1,
                      opacity: theme === "light" ? 0.1 : 1,
                      mixBlendMode: "normal",
                    }}
                  />
                </div>
                <SmartAIButton
                  isActive={showSmartAI}
                  onClick={() => {
                    setShowSmartAI(!showSmartAI);
                    setShowCockpit(false);
                  }}
                />
                {showSmartAI && (
                  <SmartAIMenu
                    isOpen={showSmartAI}
                    onClose={() => setShowSmartAI(false)}
                    onSmartAIChange={(newSettings) => {
                      setSmartAISettings((prev) => {
                        // Check if any values are different
                        const hasChanges = Object.keys(newSettings).some(
                          (key) => prev[key] !== newSettings[key]
                        );

                        return hasChanges ? newSettings : prev;
                      });
                    }}
                    initialSettings={smartAISettings}
                  />
                )}

                <button
                  type="button"
                  onClick={() => {
                    setShowCockpit(!showCockpit);
                    setShowSmartAI(false);
                  }}
                  title="Advanced Settings"
                  style={{
                    position: "relative",
                    zIndex: 2,
                    background: "none",
                    border: "none",
                    padding: "8px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    marginRight: "2px",
                  }}
                >
                  <svg
                    fill={showCockpit ? "#7B68EE" : "#ADACBF"}
                    height="24px"
                    width="24px"
                    version="1.1"
                    id="Layer_1"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                    viewBox="0 0 297 297"
                    xmlSpace="preserve"
                    transform="rotate(45)"
                    stroke={showCockpit ? "#7B68EE" : "#ADACBF"}
                  >
                    <g>
                      <path d="M147.655,71.797c-17.947,0-32.548,14.601-32.548,32.548c0,17.947,14.601,32.548,32.548,32.548 c17.947,0,32.548-14.601,32.548-32.548C180.203,86.398,165.603,71.797,147.655,71.797z M147.655,121.576 c-9.501,0-17.231-7.73-17.231-17.231c0-9.501,7.73-17.231,17.231-17.231c9.501,0,17.231,7.73,17.231,17.231 C164.886,113.846,157.156,121.576,147.655,121.576z"></path>
                      <path d="M245.437,183.301c-9.288-14.255-20.467-20.807-26.304-23.446c3.134-20.065,4.186-40.095,1.127-57.195 C209.149,40.542,155.333,3.222,153.048,1.663c-3.253-2.217-7.533-2.217-10.786,0c-2.285,1.558-56.101,38.879-67.211,100.996 c-3.096,17.312-1.979,37.627,1.246,57.941c-6.122,3.043-16.221,9.637-24.734,22.7c-16,24.555-18.687,59.459-7.985,103.742 c0.74,3.065,2.943,5.569,5.888,6.695c1.107,0.423,2.264,0.63,3.416,0.63c1.916,0,3.815-0.575,5.436-1.692l46.663-32.181 c1.24,3.104,2.056,5.039,2.273,5.549c1.502,3.534,4.971,5.828,8.81,5.828h22.018v15.556c0,5.287,4.286,9.573,9.573,9.573 s9.573-4.286,9.573-9.573v-15.556h22.018c3.839,0,7.308-2.294,8.81-5.828c0.237-0.558,1.193-2.828,2.637-6.464l47.989,33.096 c1.621,1.117,3.521,1.692,5.436,1.692c1.152,0,2.31-0.208,3.416-0.63c2.945-1.126,5.147-3.629,5.888-6.695 C264.123,242.76,261.437,207.856,245.437,183.301z M147.655,21.617c6.194,5.032,16.822,14.516,27.123,27.684h-54.246 C130.835,36.13,141.464,26.647,147.655,21.617z M59.265,268.765c-10.448-57.725,9.367-80.269,20.823-88.232 c5.157,23.501,12.272,45.624,17.937,61.501L59.265,268.765z M172.791,252.725h-15.563V176.86c0-5.287-4.286-9.573-9.573-9.573 c-5.287,0-9.573,4.286-9.573,9.573v75.865h-15.567c-8.574-21.734-37.118-99.175-28.618-146.695 c2.785-15.569,8.839-29.477,16.051-41.413h75.413c7.212,11.936,13.267,25.844,16.051,41.413 C209.901,153.495,181.364,230.978,172.791,252.725z M237.736,268.765l-40.116-27.666c5.688-16.028,12.744-38.143,17.816-61.546 C226.546,186.558,248.648,208.471,237.736,268.765z"></path>
                    </g>
                  </svg>
                </button>
                {showCockpit && (
                  <CockpitMenu
                    isOpen={showCockpit}
                    onClose={() => setShowCockpit(false)}
                    onParametersChange={(newParams) => {
                      // Only update if values actually changed
                      setModelParameters((prev) => {
                        // Check if any values are different
                        const hasChanges = Object.keys(newParams).some(
                          (key) => prev[key] !== newParams[key]
                        );

                        return hasChanges ? newParams : prev;
                      });
                    }}
                    initialParameters={modelParameters}
                  />
                )}

                <textarea
                  name="inputPrompt"
                  className="inputPrompttTextarea"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={handleKeyDown}
                  ref={inputRef}
                  placeholder={
                    isLoading ? "Waiting for response..." : "Send a message..."
                  }
                  disabled={isLoading}
                  style={{ position: "relative", zIndex: 2 }}
                  rows="1"
                />

                <ModelSelector
                  selectedModel={selectedModel}
                  setSelectedModel={setSelectedModel}
                  models={models}
                  providerIconIndexMapping={providerIconIndexMapping}
                  isModelSelecting={isModelSelecting}
                  currentModelIcon={currentModelIcon}
                  theme={theme}
                  isLoading={isLoading}
                  handleModelAutoSelect={handleModelAutoSelect}
                />

                <ImageUploader
                  fileInputRef={fileInputRef}
                  selectedImage={selectedImage}
                  setSelectedImage={setSelectedImage}
                  setImageData={setImageData}
                  imageLoading={imageLoading}
                  handleImageUpload={handleImageUpload}
                />

                {/* Add the MicrophoneButton here */}
                <MicrophoneButton
                  onTranscriptionComplete={handleTranscriptionComplete}
                  disabled={isLoading}
                  ref={micButtonRef}
                />

                <button
                  aria-label="form submit"
                  type="button"
                  onClick={handleSubmit}
                  disabled={isLoading || !prompt}
                  className="relative z-20 group"
                >
                  <img
                    src={SubmitArrow}
                    alt="Submit"
                    className="w-6 h-6 transition-all group-hover:filter group-hover:invert group-hover:brightness-100 group-hover:grayscale"
                  />
                </button>
              </div>
            </motion.form>
          )}
        </AnimatePresence>
      </section>
    </>
  );
};

export default Home;
